/* global t, runtime, mergeState, safeArray, num, buildLedgerLines, bankReconMonthRange, RECV_AGE_BUCKETS, RECV_SUBLEDGER_ACCOUNT, recvBuildItems, recvRound, recvDuplicateKey */
// KUNDEFORDRINGER — lesemodellen som samler reskontroskjermen
//
// Postene selv kommer fra `backend-receivables-open-items.jsx`. Her settes de
// sammen til det flaten faktisk viser: kundene bak dem, kontrollkontoene de
// hører til, aldersfordelingen, de automatiske kontrollene og oppsummeringen.
//
// Modellen er READ-ONLY og har ingen mockdata. Har foretaket ingen åpne poster,
// er lista tom og flaten sier det.

// Kunden, ikke fakturaen. Konsentrasjonsrisiko er hvor stor andel én motpart
// utgjør — et tall byrået skal se, ikke en regel appen håndhever.
const recvBuildCustomers = (state, items) => {
  const limitByName = new Map();
  for (const customer of safeArray(state.customers)) {
    const limit = Number(customer?.creditLimit);
    if (Number.isFinite(limit) && customer?.creditLimit !== null && customer?.creditLimit !== '') {
      limitByName.set(String(customer.name || '').trim().toLowerCase(), limit);
    }
  }
  const byName = new Map();
  for (const item of items) {
    const key = item.customerId || item.organizationNumber || String(item.customer).trim().toLowerCase();
    if (!byName.has(key)) {
      byName.set(key, {
        name: item.customer,
        customerId: item.customerId || '',
        organizationNumber: item.organizationNumber,
        openBalance: 0,
        overdueBalance: 0,
        oldestDays: null,
        itemCount: 0,
        oldItemCount: 0,
        flags: new Set(),
        collectionLabel: item.collection.label,
        collectionRank: -1,
      });
    }
    const group = byName.get(key);
    group.openBalance = recvRound(group.openBalance + item.openAmount);
    group.itemCount += 1;
    if (item.daysOverdue !== null && item.daysOverdue > 0) {
      group.overdueBalance = recvRound(group.overdueBalance + item.openAmount);
      if (group.oldestDays === null || item.daysOverdue > group.oldestDays) group.oldestDays = item.daysOverdue;
    }
    if (item.flags.includes('gammel')) group.oldItemCount += 1;
    for (const flag of item.flags) group.flags.add(flag);
    // Innfordringsstatusen kunden vises med er den strengeste av postene sine.
    const rank = item.collection.notices ? 2 : item.collection.reminders ? 1 : 0;
    if (rank > group.collectionRank) { group.collectionRank = rank; group.collectionLabel = item.collection.label; }
  }
  return [...byName.values()].map((group) => {
    const creditLimit = limitByName.has(String(group.name).trim().toLowerCase())
      ? limitByName.get(String(group.name).trim().toLowerCase())
      : null;
    const risk = group.flags.has('tapsrisiko') || group.flags.has('inkasso')
      || (creditLimit !== null && creditLimit > 0 && group.openBalance > creditLimit) ? 'hoy'
      : (group.oldestDays || 0) > 30 ? 'middels' : 'lav';
    return {
      ...group,
      flags: [...group.flags],
      creditLimit,
      utilization: creditLimit !== null && creditLimit > 0
        ? Math.round((group.openBalance / creditLimit) * 100) : null,
      overLimit: creditLimit !== null && creditLimit > 0 && group.openBalance > creditLimit,
      risk,
    };
  }).sort((left, right) => right.openBalance - left.openBalance);
};

// Kontrollkontoene i området, med reskontrosaldoen ved siden av den bokførte.
// Reskontroen ligger på appens egen kundefordringskonto — en annen konto i
// området har ingen underliggende reskontro, og da er differansen ikke et avvik
// men et fravær av grunnlag flaten må kunne si fra om.
const recvControlAccounts = (accounts, subledgerTotal) => safeArray(accounts).map((account) => {
  const hasSubledger = String(account.accountId) === RECV_SUBLEDGER_ACCOUNT;
  const booked = recvRound(account.bookedBalance);
  return {
    accountId: account.accountId,
    accountName: account.accountName || '',
    bookedBalance: booked,
    hasSubledger,
    subledgerBalance: hasSubledger ? subledgerTotal : null,
    difference: hasSubledger ? recvRound(booked - subledgerTotal) : null,
  };
});

// Hovedbokslinjer på kontrollkontoene som ikke kommer fra en faktura eller en
// innbetaling. De har ingen reskontropost å høre til, og er derfor det
// vanligste opphavet til en differanse ingen finner.
const recvDirectPostings = (state, accounts, range) => {
  const codes = safeArray(accounts).map((account) => String(account.accountId)).filter(Boolean);
  if (!codes.length) return [];
  return buildLedgerLines({ from: range.from, to: range.to, accountCodes: codes, state })
    .filter((line) => !/^(invoice|payment)/i.test(String(line.sourceType || '')));
};

const buildReceivablesReconciliationView = ({ periodId, accounts = [], state: suppliedState } = {}) => {
  const range = bankReconMonthRange(periodId);
  const state = suppliedState || mergeState(runtime.appState);
  if (!range) {
    return {
      periodId, from: '', to: '', items: [], customers: [], aging: [], controlAccounts: [],
      directPostings: [], counts: {}, checks: [], summary: {},
    };
  }
  const today = new Date().toISOString().slice(0, 10);
  const items = recvBuildItems(state, range.to, today);
  const customers = recvBuildCustomers(state, items);
  const subledgerTotal = recvRound(items.reduce((sum, item) => sum + item.openAmount, 0));
  const controlAccounts = recvControlAccounts(accounts, subledgerTotal);
  const directPostings = recvDirectPostings(state, accounts, range);
  // Saldoen ved inngangen til perioden er de samme postene sett per dagen før.
  // Den regnes ikke ut av forrige måneds rapport: da ville to tall for det
  // samme kunne sprike.
  const openingItems = recvBuildItems(state, recvIsoBefore(range.from), today);
  const openingBalance = recvRound(openingItems.reduce((sum, item) => sum + item.openAmount, 0));

  const has = (flag) => items.filter((item) => item.flags.includes(flag)).length;
  const duplicateGroups = new Set(items.filter((item) => item.duplicateOf.length).map(recvDuplicateKey)).size;
  const counts = {
    total: items.length,
    invoices: items.filter((item) => !item.isCredit).length,
    creditNotes: items.filter((item) => item.isCredit).length,
    customers: customers.length,
    flagged: items.filter((item) => item.flags.some((flag) => flag !== 'innbetalt_etter')).length,
    gammel: has('gammel'),
    forfalt30: has('forfalt30'),
    negativ: has('negativ'),
    duplikat: has('duplikat'),
    duplikatGrupper: duplicateGroups,
    betaltApen: has('betalt_apen'),
    kreditnotaApen: has('kreditnota_apen'),
    inkasso: has('inkasso'),
    tapsrisiko: has('tapsrisiko'),
    manglerDok: has('mangler_dok'),
    innbetaltEtter: has('innbetalt_etter'),
    kreditnotaEtter: has('kreditnota_etter'),
    direktefort: directPostings.length,
    overKredittgrense: customers.filter((customer) => customer.overLimit).length,
    hoyRisiko: customers.filter((customer) => customer.risk === 'hoy').length,
    flereGamlePoster: customers.filter((customer) => customer.oldItemCount > 1).length,
  };

  const aging = RECV_AGE_BUCKETS.map((bucket) => {
    const rows = items.filter((item) => item.ageBucket === bucket.key);
    return {
      ...bucket,
      count: rows.length,
      amount: recvRound(rows.reduce((sum, row) => sum + row.openAmount, 0)),
    };
  }).filter((bucket) => bucket.count > 0);

  const withSubledger = controlAccounts.filter((account) => account.hasSubledger);
  const checks = [
    {
      key: 'hovedbok_reskontro',
      label: t('rcr.check.ledgerVsSubledger', 'Hovedbok mot reskontro'),
      value: withSubledger.filter((account) => Math.abs(account.difference) > 0.005).length,
      note: withSubledger.length ? '' : t('rcr.check.noSubledgerNote', 'Ingen kontrollkonto med reskontro i området'),
    },
    { key: 'gammel', label: t('rcr.check.old', 'Gamle poster over 90 dager'), value: counts.gammel },
    { key: 'forfalt30', label: t('rcr.check.overdue30', 'Forfalte poster over 30 dager'), value: counts.forfalt30 },
    { key: 'negativ', label: t('rcr.check.negative', 'Negative kundesaldoer'), value: counts.negativ },
    { key: 'betalt_apen', label: t('rcr.check.partlyPaid', 'Delbetalt, men fortsatt åpen'), value: counts.betaltApen },
    { key: 'kreditnota_apen', label: t('rcr.check.creditNoteOpen', 'Kreditnotaer som ikke er utlignet'), value: counts.kreditnotaApen },
    { key: 'duplikat', label: t('rcr.check.duplicates', 'Mulige duplikater'), value: counts.duplikat },
    { key: 'inkasso', label: t('rcr.check.collection', 'Inkassovarsler sendt'), value: counts.inkasso },
    { key: 'tapsrisiko', label: t('rcr.check.lossRisk', 'Poster med tapsrisiko'), value: counts.tapsrisiko },
    { key: 'innbetalt_etter', label: t('rcr.check.paidAfter', 'Etterfølgende innbetaling'), value: counts.innbetaltEtter, positive: true },
    { key: 'kreditnota_etter', label: t('rcr.check.creditNoteAfter', 'Kreditnota etter periodedato'), value: counts.kreditnotaEtter },
    { key: 'direktefort', label: t('rcr.check.direct', 'Direkteføringer på kontrollkonto'), value: counts.direktefort },
    { key: 'mangler_dok', label: t('rcr.check.missingDoc', 'Manglende fakturadokument'), value: counts.manglerDok },
  ];

  return {
    periodId,
    from: range.from,
    to: range.to,
    subsequentTo: today,
    items,
    customers,
    aging,
    controlAccounts,
    directPostings,
    counts,
    checks,
    summary: {
      subledgerTotal,
      bookedTotal: recvRound(controlAccounts.reduce((sum, account) => sum + account.bookedBalance, 0)),
      subledgerBookedTotal: recvRound(withSubledger.reduce((sum, account) => sum + account.bookedBalance, 0)),
      difference: recvRound(withSubledger.reduce((sum, account) => sum + account.bookedBalance, 0) - subledgerTotal),
      hasSubledgerAccount: withSubledger.length > 0,
      openingBalance,
      change: recvRound(subledgerTotal - openingBalance),
      lossExposure: recvRound(items.filter((item) => item.flags.includes('tapsrisiko'))
        .reduce((sum, item) => sum + item.openAmount, 0)),
      topFiveShare: (() => {
        const total = customers.reduce((sum, customer) => sum + customer.openBalance, 0);
        if (Math.abs(total) < 0.005) return null;
        return Math.round((customers.slice(0, 5).reduce((sum, customer) => sum + customer.openBalance, 0) / total) * 100);
      })(),
    },
  };
};

const buildCustomerReceivablesSnapshot = ({ asOf, state: suppliedState } = {}) => {
  const state = suppliedState || mergeState(runtime.appState);
  const date = String(asOf || '').slice(0, 10);
  if (!date) return [];
  return recvBuildCustomers(state, recvBuildItems(state, date, date));
};

const recvIsoBefore = (iso) => {
  const date = new Date(String(iso) + 'T00:00:00');
  if (Number.isNaN(date.getTime())) return iso;
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};

Object.assign(window, { buildReceivablesReconciliationView, buildCustomerReceivablesSnapshot, recvControlAccounts, recvBuildCustomers });
